import type { RefObject } from "react";
import { noop } from "es-toolkit";
import type { useChatStream } from "./AiToolboxSection.hooks.tsx";
import AiAssistantGraphDebug from "./components/AiAssistantGraphDebug/AiAssistantGraphDebug";
import AiAssistantHeaderActions from "./components/AiAssistantHeaderActions/AiAssistantHeaderActions";
import AiAssistantMaximizedShell from "./components/AiAssistantMaximizedShell/AiAssistantMaximizedShell";
import AiAssistantMessages from "./components/AiAssistantMessages/AiAssistantMessages";
import AiAssistantStreamError from "./components/AiAssistantStreamError/AiAssistantStreamError.tsx";
import AiAssistantThreadIdDebug from "./components/AiAssistantThreadIdDebug/AiAssistantThreadIdDebug";
import type { ChatInputHandle } from "./components/ChatInput/ChatInput.types";
import ChatInputArea from "./components/ChatInputArea/ChatInputArea";

export type AiToolboxSectionViewModel = Pick<
  ReturnType<typeof useChatStream>,
  | "messages"
  | "streamingContent"
  | "isLoading"
  | "isThreadLoading"
  | "error"
  | "send"
  | "stop"
  | "retry"
  | "retryLast"
  | "switchMessageBranch"
  | "context"
  | "editingMessageId"
  | "startEditMessage"
  | "cancelEditMessage"
  | "submitEditMessage"
  | "sessions"
  | "activeSessionId"
  | "createNewSession"
  | "switchSession"
  | "deleteSession"
  | "renameSession"
  | "assistantId"
  | "assistantIds"
  | "setAssistantId"
>;

export interface AiToolboxSectionViewProps {
  chatStream: AiToolboxSectionViewModel;
  isMaximized: boolean;
  onMaximize?: () => void;
  onCollapse?: () => void;
  inputRef?: RefObject<ChatInputHandle | null>;
}

export function AiToolboxSectionView({
  chatStream,
  isMaximized,
  onMaximize = noop,
  onCollapse = noop,
  inputRef,
}: AiToolboxSectionViewProps) {
  const { isLoading, error, send, stop } = chatStream;

  const isInitialState = chatStream.messages.length === 0;
  const lastHumanContext = chatStream.messages.findLast(
    (msg) => msg.type === "human"
  )?.context;

  const contextChanged =
    !isInitialState && !!lastHumanContext && lastHumanContext !== chatStream.context;

  const chatBody = (
    <>
      <AiAssistantGraphDebug
        assistantId={chatStream.assistantId}
        assistantIds={chatStream.assistantIds}
        onChange={chatStream.setAssistantId}
      />
      <AiAssistantThreadIdDebug threadId={chatStream.activeSessionId} />

      <AiAssistantMessages {...chatStream} />

      {error && (
        <AiAssistantStreamError
          {...{ error, isLoading, retryLast: chatStream.retryLast }}
        />
      )}

      <ChatInputArea
        onSubmit={send}
        onStop={stop}
        isLoading={isLoading}
        inputRef={inputRef}
        showContext={contextChanged || isInitialState}
      />
    </>
  );

  if (isMaximized) {
    return (
      <AiAssistantMaximizedShell
        onCollapse={onCollapse}
        sessions={chatStream.sessions}
        activeSessionId={chatStream.activeSessionId}
        onSwitchSession={chatStream.switchSession}
        onDeleteSession={chatStream.deleteSession}
        onRenameSession={chatStream.renameSession}
        onCreateNewSession={chatStream.createNewSession}
      >
        {chatBody}
      </AiAssistantMaximizedShell>
    );
  }

  return (
    <div
      className="flex flex-col h-full"
      data-ph-capture-attribute-feature="ai_assistant"
    >
      <header className="flex items-center justify-between pb-3">
        <h1 className="text-base font-medium">AI Assistant</h1>
        <aside className="flex items-center gap-1">
          <AiAssistantHeaderActions
            sessions={chatStream.sessions}
            activeSessionId={chatStream.activeSessionId}
            onSwitchSession={chatStream.switchSession}
            onDeleteSession={chatStream.deleteSession}
            onRenameSession={chatStream.renameSession}
            onCreateNewSession={chatStream.createNewSession}
            onMaximize={onMaximize}
          />
        </aside>
      </header>

      {chatBody}
    </div>
  );
}
